import { OFF_MARKET_ROUTE, RouteName } from 'components/dynamic-page/route-matchers';
import * as GoogleTagManager from './providers/google-tag-manager';
import * as DevelopmentAnalytics from './providers/development-reporter';
import { NextWebVitalsMetric } from 'next/app';
import { isProduction } from 'utils/environment';
import storage from 'utils/storage';
import { v4 as uuid } from 'uuid';

/**
 * AnalyticsController - Centralized analytics tracking controller
 *
 * This controller provides a unified interface for tracking analytics events across the application.
 * It acts as a facade that delegates tracking to appropriate providers based on the environment:
 * - Google Tag Manager (GTM) for production, staging, and stage-two environments
 * - Development Reporter for development environment (console logging)
 *
 * The controller ensures consistent analytics tracking regardless of the underlying provider,
 * making it easy to switch between different analytics services or add new ones.
 *
 * ## Available Methods
 *
 * - `trackPageView(page)` - Track page views with special handling for off-market pages
 * - `trackWebVitals(metric)` - Track performance metrics (LCP, FID, CLS, etc.)
 * - `trackDelayedPageView()` - Track page views that occur after initial load
 * - `trackAppLoadedEvent()` - Track when the application is fully loaded
 * - `trackEvent(event, category, label, value)` - Track custom events with optional parameters
 * - `trackEventConditionally(event, condition)` - Track custom events conditionally based on boolean conditions
 * - `trackConnectionRequest()` - Track connection request submissions (conversion events)
 * - `trackGrowthbookEvent(experimentId, variationId)` - Track A/B testing and experimentation
 *
 * ## Environment Behavior
 *
 * - **Production/Staging**: Events are sent to Google Tag Manager for analysis
 * - **Development**: Events are logged to the console for debugging
 *
 * ## Usage Guidelines
 *
 * 1. Use `trackPageView()` for standard page navigation
 * 2. Use `trackEvent()` for user interactions and custom events
 * 3. Use `trackEventConditionally()` when you need to track events based on runtime conditions
 * 4. Use `trackWebVitals()` in your Next.js app for performance monitoring
 * 5. Use `trackGrowthbookEvent()` when users are exposed to experiments
 * 6. Use `trackConnectionRequest()` for lead generation tracking
 *
 * @example
 * ```typescript
 * // Track a page view
 * AnalyticsController.trackPageView('/search/toronto-condos');
 *
 * // Track a custom event
 * AnalyticsController.trackEvent('listing_favorited', 'User Interaction', 'condo_listing', 1);
 *
 * // Track web vitals
 * AnalyticsController.trackWebVitals({ name: 'LCP', value: 1200, delta: 1200 });
 * ```
 */
export const AnalyticsController = {
  /**
   * Tracks a page view event for analytics
   *
   * This method handles different types of page views based on the route type:
   * - Special handling for off-market pages (uses dedicated off-market tracking)
   * - Standard page view tracking for all other routes
   *
   * The method automatically delegates to both Google Tag Manager and Development Reporter
   * based on the current environment.
   *
   * @param page The page URL or route name to track. Can be a string URL or a {@link RouteName}
   *
   * @example
   * ```typescript
   * // Track a standard page view
   * AnalyticsController.trackPageView('/search/toronto-condos');
   *
   * // Track off-market page (special handling)
   * AnalyticsController.trackPageView('off-market');
   * ```
   */
  trackPageView: (page: string | RouteName) => {
    switch (page) {
      case OFF_MARKET_ROUTE:
        GoogleTagManager.trackOffMarketPage();
        DevelopmentAnalytics.trackOffMarketPage();
        break;
      default:
        GoogleTagManager.trackCurrentPageView(page);
        DevelopmentAnalytics.trackCurrentPageView(page);
        return;
    }
  },
  /**
   * Tracks web vitals metrics for performance monitoring
   *
   * Web vitals are key metrics that measure user experience on web pages. This method
   * tracks various performance metrics including Core Web Vitals (LCP, FID, CLS) and
   * Next.js specific metrics.
   *
   * In production environments, metrics are sent to Google Tag Manager with the format
   * `web-vitals-{metricName}`. In development, they are logged to the console with
   * color-coded ratings (good: green, needs-improvement: orange, poor: red).
   *
   * @param metric The web vitals metric object from Next.js
   */
  trackWebVitals: (metric: NextWebVitalsMetric) => {
    GoogleTagManager.trackWebVitals(`web-vitals-${metric.name}`, metric.value);
    DevelopmentAnalytics.trackWebVitals(metric);
  },
  /**
   * Tracks a delayed page view event.
   */
  trackDelayedPageView: () => {
    GoogleTagManager.trackDelayedPageView();
    DevelopmentAnalytics.trackDelayedPageView();
  },
  /**
   * Tracks when the application has fully loaded
   *
   * This method is typically called after the application has completed its initial
   * setup and is ready for user interaction. It's useful for measuring application
   * startup time and ensuring that all critical resources have been loaded.
   */
  trackAppLoadedEvent: () => {
    GoogleTagManager.trackAppLoadedEvent();
    DevelopmentAnalytics.trackAppLoadedEvent();
  },
  /**
   * Tracks a custom analytics event
   *
   * This is the most flexible method for tracking custom user interactions and events.
   * It allows you to track any event with optional categorization, labeling, and numeric values.
   *
   * @param event The name of the event to track (e.g., 'button_click', 'form_submit')
   * @param category The category to group this event under (defaults to 'All')
   * @param label Optional label to provide additional context about the event
   * @param value Optional numeric value associated with the event
   *
   * @example
   * ```typescript
   * // Track a simple button click
   * AnalyticsController.trackEvent('search_button_clicked');
   *
   * // Track with category and label
   * AnalyticsController.trackEvent('listing_favorited', 'User Interaction', 'condo_listing');
   *
   * // Track with all parameters
   * AnalyticsController.trackEvent('purchase_completed', 'E-commerce', 'premium_plan', 99.99);
   *
   * // Track form submission
   * AnalyticsController.trackEvent('contact_form_submitted', 'Lead Generation', 'property_inquiry');
   * ```
   */
  trackEvent: (event: string, category = 'All', label?: string, value?: number) => {
    GoogleTagManager.trackEvent(event, category, label, value);
    DevelopmentAnalytics.trackEvent(event, category, label, value);
  },

  /**
   * Tracks a custom analytics event conditionally based on a boolean condition
   *
   * This method provides conditional event tracking, allowing you to track events only when
   * specific conditions are met. This is useful for A/B testing, feature flags, user state
   * checks, or any scenario where you want to track events based on runtime conditions.
   *
   * The condition can be either a static boolean value or a function that returns a boolean.
   * If the condition evaluates to true, the event will be tracked using both Google Tag Manager
   * and Development Reporter (based on environment). If false, no tracking occurs.
   *
   * @param event An object containing the event details:
   *   - `event` (string): The name of the event to track (e.g., 'button_click', 'form_submit')
   *   - `category` (string, optional): The category to group this event under (defaults to 'All')
   *   - `label` (string, optional): Optional label to provide additional context about the event
   *   - `value` (number, optional): Optional numeric value associated with the event
   * @param condition Either a boolean value or a function that returns a boolean. The event
   *                  will only be tracked if this condition evaluates to true.
   *
   * @example
   * ```typescript
   * // Track event based on a simple boolean condition
   * const isUserLoggedIn = true;
   * AnalyticsController.trackEventConditionally(
   *   { event: 'logged_in_feature_accessed', category: 'User Interaction' },
   *   isUserLoggedIn
   * );
   *
   * // Track event based on a function condition
   * AnalyticsController.trackEventConditionally(
   *   { event: 'experiment_variant_viewed', category: 'A/B Testing', label: 'variant_a' },
   *   () => user.isInExperiment('header_redesign') && user.getVariant() === 'a'
   * );
   * ```
   */
  trackEventConditionally: (event: { event: string; category?: string; label?: string; value?: number }, condition: boolean | (() => boolean)) => {
    const { event: eventName, category = 'All', label, value } = event;
    if (typeof condition === 'function' ? condition() : condition) {
      GoogleTagManager.trackEvent(eventName, category, label, value);
      DevelopmentAnalytics.trackEvent(eventName, category, label, value);
    }
  },

  /**
   * This method tracks when users view real estate listings that are associated with CREA.
   *
   * The function extracts the CREA listing ID from the provided CREA URL and sends a view
   * event to the CREA analytics endpoint. In production environments, the event is sent to
   * the official CREA analytics service. In non-production environments, the event is logged
   * to the console for debugging purposes.
   *
   * The tracking includes:
   * - CREA Listing ID (extracted from the realtor.ca URL)
   * - Destination ID (35, representing Zoocasa)
   * - Event Type (view)
   * - Unique UUID for the user session
   *
   * @param listingId The internal Zoocasa listing ID for reference and error logging
   * @param creaUrl The full CREA URL from realtor.ca (e.g., 'https://www.realtor.ca/real-estate/12345/123-main-st')
   *                 If null or invalid, the event will not be tracked and an error will be logged
   *
   * @example
   * ```typescript
   * // Track a CREA view event
   * AnalyticsController.trackCreaViewEvent(12345, 'https://www.realtor.ca/real-estate/67890/456-queen-st');
   *
   * // Handle case where CREA URL is not available
   * AnalyticsController.trackCreaViewEvent(12345, null);
   * ```
   *
   * @throws Will log an error to the console if the CREA URL is invalid or the listing ID cannot be extracted
   */
  trackCreaViewEvent: (listingId:number, creaUrl: string | null) => {
    const creaListingId = creaUrl?.replace('https://www.realtor.ca/real-estate', '').split('/')?.[1];
    if (creaListingId) {
      if (isProduction) {
        const creaAnalyticsEndpoint = `https://analytics.crea.ca/LogEvents.svc/LogEvents?ListingID=${creaListingId}&DestinationID=35&EventType=view&UUID=${getCreaUuid()}`;
        fetch(creaAnalyticsEndpoint).catch();
      } else {
        console.debug(`Analytics\tEvent=crea-view,ListingID=${creaListingId},UUID=${getCreaUuid()}`);
      }
    } else {
      console.error(`CREA listing id not found for Zoocasa listing ${listingId}`);
    }
  },

  /**
   * Tracks a connection request submission
   *
   * The method tracks this as a page view event to the '/connection-request-submission' path.
   */
  trackConnectionRequest: () => {
    GoogleTagManager.trackConnectionRequest();
    DevelopmentAnalytics.trackConnectionRequest();
  },

  /**
   * Tracks A/B testing and experimentation events via GrowthBook
   *
   * This method is used to track when users are exposed to different variations
   * of experiments or A/B tests. It's essential for measuring the impact of
   * different features, designs, or content on user behavior.
   *
   * The method tracks experiment exposure with both the experiment ID and the
   * specific variation ID that the user was shown, enabling proper analysis
   * of experiment performance and statistical significance.
   *
   * @param experimentId The unique identifier for the experiment
   * @param variationId The ID of the specific variation the user was exposed to
   *
   * @example
   * ```typescript
   * AnalyticsController.trackGrowthbookEvent('area-page-map-card-fourth-card', 0);
   * AnalyticsController.trackGrowthbookEvent('map-single-cluster-experiment', 1);
   * ```
   */
  trackGrowthbookEvent: (experimentId: string, variationId: number) => {
    GoogleTagManager.trackGrowthbookEvent(experimentId, variationId);
    DevelopmentAnalytics.trackGrowthbookEvent(experimentId, variationId);
  },
} as const;

function getCreaUuid() {
  const UUID = storage.get('crea-uuid');
  if (!UUID) {
    const createdUUID = uuid();
    storage.set('crea-uuid', createdUUID);
    return createdUUID;
  }
  return UUID;
}